import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query'
import { DateRange } from 'react-day-picker'

import * as apiClient from '@/lib/api'
import { PERMISSIONS } from '@/lib/auth/roles'
import { formatDate } from '@/lib/filters/date/date-utils'
import useIsQueryEnabled from '@/lib/hooks/query-enabler'
import { useQueryResetWithRefresh } from '@/lib/hooks/query-refetch-with-reset'
import { NetworkPerformanceDataByFirm } from '@/modules/network-performance-by-firm/types'
import { useDate } from '@/stores/date-filter'
import { useToken } from '@/stores/token'
import { QUERY_KEY_NETWORK_PERFORMANCE_DATA_BY_FIRM } from '@/utils/keys'

function fetchNetworkPerformanceDataByFirm(date: DateRange): Promise<NetworkPerformanceDataByFirm> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')

  return apiClient.get(`/firm/network/performance/network-performance-by-firm?&to=${to}&from=${from}`)
}

export const networkPerformanceDataByFirmQueryOptions = (
  date: DateRange,
) =>
  queryOptions({
    queryKey: [QUERY_KEY_NETWORK_PERFORMANCE_DATA_BY_FIRM, date],
    queryFn: () => fetchNetworkPerformanceDataByFirm(date),
    placeholderData: keepPreviousData
  });

export const useNetworkPerformanceDataByFirm = () => {
  const { date } = useDate()
  const { permissions } = useToken()

  const queryOptions = networkPerformanceDataByFirmQueryOptions(date)
  queryOptions.enabled = useIsQueryEnabled(permissions.includes(PERMISSIONS.IS_NETWORK_VIEW_ON_NETWORK))

  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}